/**
 * @fileoverview Persistencia mínima de la sesión del participante en
 * sessionStorage (NUNCA localStorage; la decisión es por privacidad:
 * al cerrar la pestaña la sesión deja de existir en el dispositivo).
 *
 * Solo guardamos lo estrictamente necesario para sobrevivir a una
 * recarga de página: session_token + codigo. Todo lo demás (votos,
 * brechas, datos demográficos) se vuelve a pedir al backend o se
 * mantiene solo en memoria.
 */

const KEY_TOKEN = "nl.session_token";
const KEY_CODIGO = "nl.codigo";
const KEY_EXPIRA = "nl.expira_en";
const KEY_PARTICIPACION = "nl.participacion_id";
const KEY_FORUM = "nl.forum_id";
const KEY_ALCALDIA_FORO = "nl.alcaldia_foro";
const KEY_INDICE = "nl.indice_actual";

/**
 * @typedef {Object} SessionPersisted
 * @property {string} session_token
 * @property {string} codigo
 * @property {string} expira_en  ISO datetime
 * @property {string} [participacion_id]
 * @property {string} [forum_id]
 * @property {string} [alcaldia_foro]
 * @property {number} [indice_actual]
 */

/**
 * Lee la sesión del sessionStorage. Devuelve null si no existe o si
 * expiró.
 * @returns {SessionPersisted | null}
 */
export function leerSesion() {
    try {
        const token = sessionStorage.getItem(KEY_TOKEN);
        const codigo = sessionStorage.getItem(KEY_CODIGO);
        const expira = sessionStorage.getItem(KEY_EXPIRA);
        if (!token || !codigo || !expira) return null;

        if (new Date(expira).getTime() < Date.now()) {
            limpiarSesion();
            return null;
        }
        const idxRaw = sessionStorage.getItem(KEY_INDICE);
        const idxParsed = idxRaw != null ? parseInt(idxRaw, 10) : NaN;
        return {
            session_token: token,
            codigo,
            expira_en: expira,
            participacion_id: sessionStorage.getItem(KEY_PARTICIPACION) || undefined,
            forum_id: sessionStorage.getItem(KEY_FORUM) || undefined,
            alcaldia_foro: sessionStorage.getItem(KEY_ALCALDIA_FORO) || undefined,
            indice_actual: Number.isFinite(idxParsed) && idxParsed >= 0 ? idxParsed : undefined,
        };
    } catch (e) {
        // intencional: en modo privado / cookies bloqueadas sessionStorage
        // puede lanzar al leer. Devolvemos null para flujo sin sesión.
        console.error("[session] leerSesion falló:", e);
        return null;
    }
}

/**
 * @param {SessionPersisted} s
 */
export function guardarSesion(s) {
    try {
        sessionStorage.setItem(KEY_TOKEN, s.session_token);
        sessionStorage.setItem(KEY_CODIGO, s.codigo);
        sessionStorage.setItem(KEY_EXPIRA, s.expira_en);
        if (s.participacion_id) sessionStorage.setItem(KEY_PARTICIPACION, s.participacion_id);
        if (s.forum_id) sessionStorage.setItem(KEY_FORUM, s.forum_id);
        if (s.alcaldia_foro) sessionStorage.setItem(KEY_ALCALDIA_FORO, s.alcaldia_foro);
        if (typeof s.indice_actual === "number" && s.indice_actual >= 0) {
            sessionStorage.setItem(KEY_INDICE, String(s.indice_actual));
        }
    } catch (e) {
        // intencional: best-effort. sessionStorage puede lanzar QuotaExceededError
        // o estar deshabilitado; no rompemos el flujo del participante por esto.
        console.error("[session] guardarSesion falló:", e);
    }
}

export function limpiarSesion() {
    try {
        sessionStorage.removeItem(KEY_TOKEN);
        sessionStorage.removeItem(KEY_CODIGO);
        sessionStorage.removeItem(KEY_EXPIRA);
        sessionStorage.removeItem(KEY_PARTICIPACION);
        sessionStorage.removeItem(KEY_FORUM);
        sessionStorage.removeItem(KEY_ALCALDIA_FORO);
        sessionStorage.removeItem(KEY_INDICE);
    } catch (e) {
        // intencional: best-effort cleanup; si sessionStorage no está
        // disponible no hay nada que limpiar.
        console.error("[session] limpiarSesion falló:", e);
    }
}
